// Editar una tabla de markdown a mano es lo que se le rompe al tenant: agrega
// una columna en el encabezado, la fila de guiones deja de cuadrar y la tabla
// desaparece del catálogo. Estas funciones son puras a propósito (no tocan el
// DOM ni el textarea): el diálogo trabaja sobre filas/columnas y acá se pasa a
// markdown GFM y de vuelta.
const ALIGNMENTS = ["left", "center", "right"];

const ALIGNMENT_MARKERS = {
    center: ":---:",
    left: "---",
    right: "---:"
};

const DELIMITER_CELL = /^:?-+:?$/;

const DEFAULT_ALIGNMENT = "left";

function toAlignment(marker) {
    const cell = marker.trim();
    const opensWithColon = cell.startsWith(":");
    const closesWithColon = cell.endsWith(":");
    if (opensWithColon && closesWithColon) return "center";
    if (closesWithColon) return "right";
    return DEFAULT_ALIGNMENT;
}

// Un "\|" dentro de una celda es un pipe literal, no un separador: hay que
// partir la fila a mano en vez de con split("|").
function splitRow(line) {
    const source = line.trim();
    const cells = [];
    let current = "";

    for (let index = 0; index < source.length; index += 1) {
        const char = source[index];
        if (char === "\\" && source[index + 1] === "|") {
            current += "|";
            index += 1;
            continue;
        }
        if (char === "|") {
            cells.push(current);
            current = "";
            continue;
        }
        current += char;
    }
    cells.push(current);

    if (cells.length > 1 && cells[0].trim() === "") cells.shift();
    if (cells.length > 1 && cells[cells.length - 1].trim() === "") cells.pop();

    return cells.map((cell) => cell.trim());
}

function isDelimiterRow(cells) {
    return cells.length > 0 && cells.every((cell) => DELIMITER_CELL.test(cell));
}

function isTableLine(line) {
    return line.trim() !== "" && line.includes("|");
}

function padRow(cells, columnCount) {
    return Array.from({ length: columnCount }, (item, index) =>
        typeof cells[index] === "string" ? cells[index] : ""
    );
}

function padAlignments(alignments, columnCount) {
    return Array.from({ length: columnCount }, (item, index) =>
        ALIGNMENTS.includes(alignments[index])
            ? alignments[index]
            : DEFAULT_ALIGNMENT
    );
}

export function createEmptyTable(columnCount = 3, rowCount = 3) {
    return {
        alignments: padAlignments([], columnCount),
        rows: Array.from({ length: rowCount }, () => padRow([], columnCount))
    };
}

// Tolerante a propósito: si la fila de guiones tiene menos columnas que el
// encabezado (la tabla que el tenant ya rompió) igual se parsea, rellenando,
// para que el diálogo la pueda abrir y arreglar.
export function parseTable(source) {
    const lines = (source || "")
        .split("\n")
        .filter((line) => line.trim() !== "");
    if (lines.length < 2) return null;

    const delimiter = splitRow(lines[1]);
    if (!isDelimiterRow(delimiter)) return null;

    const body = [lines[0], ...lines.slice(2)].map(splitRow);
    const columnCount = Math.max(
        delimiter.length,
        ...body.map((cells) => cells.length)
    );

    return {
        alignments: padAlignments(delimiter.map(toAlignment), columnCount),
        rows: body.map((cells) => padRow(cells, columnCount))
    };
}

function escapeCell(cell) {
    return String(cell === undefined || cell === null ? "" : cell)
        .replace(/\r?\n/g, " ")
        .replace(/\|/g, "\\|")
        .trim();
}

export function serializeTable(table) {
    const rows = (table && table.rows) || [];
    const alignments = (table && table.alignments) || [];
    const columnCount = Math.max(
        1,
        alignments.length,
        ...rows.map((cells) => cells.length)
    );
    const toLine = (cells) =>
        `| ${padRow(cells, columnCount).map(escapeCell).join(" | ")} |`;
    const delimiter = padAlignments(alignments, columnCount)
        .map((alignment) => ALIGNMENT_MARKERS[alignment])
        .join(" | ");

    return [
        toLine(rows[0] || []),
        `| ${delimiter} |`,
        ...rows.slice(1).map(toLine)
    ].join("\n");
}

export function isEmptyTable(table) {
    const rows = (table && table.rows) || [];
    return !rows.some((cells) =>
        cells.some((cell) => String(cell || "").trim() !== "")
    );
}

// Devuelve la tabla que contiene el cursor (con el rango exacto que ocupa en el
// texto, para reemplazarla al guardar) o null si el cursor no está en una.
export function findTableAt(value, caret) {
    const source = value || "";
    if (caret < 0 || caret > source.length) return null;

    const lines = source.split("\n");
    const offsets = [];
    let cursor = 0;
    lines.forEach((line) => {
        offsets.push(cursor);
        cursor += line.length + 1;
    });

    const caretLine = lines.findIndex(
        (line, index) =>
            caret >= offsets[index] && caret <= offsets[index] + line.length
    );
    if (caretLine === -1 || !isTableLine(lines[caretLine])) return null;

    let first = caretLine;
    while (first > 0 && isTableLine(lines[first - 1])) first -= 1;
    let last = caretLine;
    while (last < lines.length - 1 && isTableLine(lines[last + 1])) last += 1;

    const block = lines.slice(first, last + 1);
    const delimiterIndex = block.findIndex(
        (line, index) => index > 0 && isDelimiterRow(splitRow(line))
    );
    if (delimiterIndex === -1) return null;

    const tableFirst = first + delimiterIndex - 1;
    if (caretLine < tableFirst) return null;

    const table = parseTable(lines.slice(tableFirst, last + 1).join("\n"));
    if (!table) return null;

    return {
        alignments: table.alignments,
        end: offsets[last] + lines[last].length,
        rows: table.rows,
        start: offsets[tableFirst]
    };
}

export const TABLE_ALIGNMENTS = ALIGNMENTS;
